import formatToCurrency from '../formatToCurrency';
import roundValue from '../roundValue';
import { CalculationAmountTaxedType, CalculationPayInType, CalculationPayOutType } from './enums';
import { ICurrency, ITaxes } from './interfaces';

export default class Taxes {
    public taxes: ITaxes;

    public currency: ICurrency;

    public format(value: number): string {
        return formatToCurrency(
            value,
            this.currency.precision,
            this.currency.template,
            this.currency.code,
        );
    }

    public configure(taxes: ITaxes, currency: ICurrency): void {
        this.taxes = taxes;
        this.currency = currency;
    }

    public get enabled(): boolean {
        return !!this.taxes;
    }

    public get schemeId(): number {
        return this.taxes?.schemeId ?? 0;
    }

    public get hasPayoutTaxRates(): boolean {
        return !!this.taxes?.payout?.rates?.length;
    }

    public get isTaxOnlyOnProfit(): boolean {
        return this.taxes.calculationAmountTaxedType === CalculationAmountTaxedType.PROFIT;
    }

    public getCalculatedWinAmountAfterPayinRaw(amount: number, totalOdd: number): number {
        const amountAfterPayin = this.getAmountAfterPayin(amount);
        let winAmount = roundValue(amountAfterPayin * totalOdd, this.currency.precision);

        if (this.isTaxOnlyOnProfit) {
            winAmount -= amountAfterPayin;
        }

        return winAmount;
    }

    public getPayoutTaxesRaw(winAmount: number): number {
        if (!this.taxes) {
            return 0;
        }

        let taxed: number;
        if (this.taxes.calculationPayOutType === CalculationPayOutType.STAIRCASE) {
            taxed = this.taxes.payout.rates
                .filter((tax) => winAmount > parseFloat(tax.from))
                .reduce((totalTaxed, tax) => {
                    const taxTo = parseFloat(tax.to);
                    const limitTo = winAmount > taxTo && taxTo !== -1 ? taxTo : winAmount;

                    return totalTaxed + (limitTo - parseFloat(tax.from)) * parseFloat(tax.rate);
                }, 0);
        } else {
            taxed = (this.getPayoutRate(winAmount) / 100) * winAmount;
        }

        return taxed;
    }

    public getPayinRate(amount: number): number {
        if (!this.taxes) {
            return 0;
        }

        const lastTax = this.taxes.payin.rates.find(
            (tax) => amount <= parseFloat(tax.to) || parseFloat(tax.to) === -1,
        );

        if (!lastTax) {
            return 0;
        }

        return roundValue(parseFloat(lastTax.rate) * 100, this.currency.precision);
    }

    public getPayoutRate(amount: number): number {
        if (!this.taxes) {
            return 0;
        }

        const lastTax = this.taxes.payout.rates.find(
            (tax) => amount <= parseFloat(tax.to) || parseFloat(tax.to) === -1,
        );

        if (!lastTax) {
            return 0;
        }

        return roundValue(parseFloat(lastTax.rate) * 100, this.currency.precision);
    }

    public getPayinTaxAmountRaw(amount: number, runsCount: number): number {
        const payinRate = this.getPayinRate(amount);

        let payinTaxAmount;

        if (this.taxes.calculationPayInType === CalculationPayInType.INCLUSIVE) {
            payinTaxAmount = (payinRate * amount) / (payinRate + 100);
        } else {
            payinTaxAmount = (payinRate / 100) * amount;
        }

        return roundValue(
            roundValue(payinTaxAmount, this.currency.precision) * runsCount,
            this.currency.precision,
        );
    }

    public getAmountAfterPayin(amount: number): number {
        return roundValue(amount - this.getPayinTaxAmountRaw(amount, 1), this.currency.precision);
    }

    public getPayinTaxAmount(amount: number, runsCount: number): string {
        const payinTaxAmountRaw = this.getPayinTaxAmountRaw(amount, runsCount);

        return this.format(payinTaxAmountRaw);
    }

    public getTaxesAmount(amount: number, totalOdd: number): number {
        const winAmount = this.getCalculatedWinAmountAfterPayinRaw(amount, totalOdd);

        const taxed = this.getPayoutTaxesRaw(winAmount);

        return roundValue(taxed, this.currency.precision);
    }

    public getBetAmountWithTaxes(amount: number, runsCount: number): string {
        const amountAfterPayin = this.getAmountAfterPayin(amount);

        return this.format(amountAfterPayin * runsCount);
    }

    public getWinAfterPayinRaw(amount: number, runsCount: number, totalOdd: number): number {
        const amountAfterPayin = this.getAmountAfterPayin(amount);

        return roundValue(amountAfterPayin * totalOdd, this.currency.precision) * runsCount;
    }

    public getWinAfterPayin(amount: number, runsCount: number, totalOdd: number): string {
        const amountAfterPayinRaw = this.getWinAfterPayinRaw(amount, runsCount, totalOdd);

        return this.format(amountAfterPayinRaw);
    }

    public getWinTaxPP(amount: number, totalOdd: number, runsCount: number): string {
        const taxesAmount = this.getTaxesAmount(amount, totalOdd);

        return this.format(taxesAmount * runsCount);
    }

    public getPossibleWinRaw(amount: number, runsCount: number, totalOdd: number): number {
        const amountAfterPayin = this.getAmountAfterPayin(amount);
        const taxesAmount = this.getTaxesAmount(amount, totalOdd);

        return roundValue(
            roundValue(amountAfterPayin * totalOdd - taxesAmount, this.currency.precision) *
                runsCount,
            this.currency.precision,
        );
    }

    public getPossibleWin(amount: number, runsCount: number, totalOdd: number): string {
        const possibleWinRaw = this.getPossibleWinRaw(amount, runsCount, totalOdd);

        return this.format(possibleWinRaw);
    }

    public isPayinTaxEatingProfit(amount: number, runsCount: number, totalOdd: number): boolean {
        if (!this.taxes || !amount) {
            return false;
        }

        return this.getWinAfterPayinRaw(amount, runsCount, totalOdd) <= amount * runsCount;
    }

    public isEatingProfit(amount: number, runsCount: number, totalOdd: number): boolean {
        if (!this.taxes || !totalOdd || !amount) {
            return false;
        }

        return this.getPossibleWinRaw(amount, runsCount, totalOdd) <= amount * runsCount;
    }
}
